import * as actions from './action-types'
import * as mutations from './mutation-types'
import * as constants from '../constants'

import dayjs from 'dayjs'
import { disableMessagesDelay, isProjectMode } from '../app.config'
import { Message, messageTypes } from '../model/messages'
import { languageToLocale } from '@/helpers/language'
import { getCustomData } from '@/helpers/util'
import { checkIfSoloSourceMessage, matchMessage } from '@/store/util'
import { i18nInstance } from '@/main'

let minDelay = 500
// noinspection JSUnusedLocalSymbols
const b4uSocketModule = {
  namespaced: false,
  state: {
    sessionId: null,
    connected: false,
    messageList: [],
    socketMessages: [],
    lastMessageLength: 0,
    lastMessageDate: null,
    latestMessageTimer: 0,
    lastMessageOccupiedDates: null,
    lastMessageSuggestions: null,
    lastMessageSemanticSuggestions: null,
    lastMessageHiddenSemanticSuggestions: null,
    lastMessageSuggestionsTitle: null,
    lastMessageSuggestionsOnly: false,
    enableChatBot: false,
    enableLiveChat: false,
    operatorTakeOver: false,
    operatorTyping: false,
    userTyping: false,
    enableInputField: false,
    connectTimeout: null,
    isRestricted: false,
    enableInputSuggestions: false,
    operatorTookOver: false,
    enabledLanguages: [],
    fileList: [],
    regeneratedMessageFrom: null,
    typingIndicatorTimeout: null,
    documentIndexedEvents: [],
    showThinkingMessage: false,
    /** { phase: 'started' } while internet-suche runs; null otherwise */
    webSearchProgress: null
  },
  actions: {
    // eslint-disable-next-line no-unused-vars
    [actions.BOT_CONNECT]: context => {},
    [actions.BOT_DISCONNECT]: () => {},
    [actions.ADD_BOT_MESSAGE]: (context, message) => {
      let delay = 0
      if (context.state.lastMessageDate) {
        delay = dayjs().diff(context.state.lastMessageDate, 'millisecond')
      }
      if (
        !(
          message.type === messageTypes.SYSTEM ||
          message.type === messageTypes.SYSTEM_NOTIFICATION
        )
      ) {
        delay = disableMessagesDelay
          ? 0
          : minDelay > delay
          ? minDelay - delay
          : 0
      }
      context.dispatch(actions.HIDE_THINKING_MESSAGE).then(() => {})
      setTimeout(() => {
        context.commit(mutations.SOCKET_ON_MESSAGE, message)
        context.state.latestMessageTimer -= 500
      }, delay + context.state.latestMessageTimer)
      context.state.latestMessageTimer += 500
      context.state.lastMessageDate = new Date().getTime()
    },
    [actions.ADD_BOT_MESSAGE_PART]: (context, message) => {
      context.commit(mutations.SOCKET_ON_REPLACE_LAST_MESSAGE, message)
      context.state.lastMessageDate = new Date().getTime()
    },
    [actions.ADD_OPERATOR_MESSAGE]: (context, message) => {
      context.state.operatorTyping = false
      context.commit(mutations.SOCKET_ON_MESSAGE, message)
      context.state.lastMessageDate = new Date().getTime()
      context.state.lastMessageLength = message.text.length
    },
    [actions.UPDATE_SUGGESTIONS]: (context, suggestionsMessage) => {
      if (!suggestionsMessage && context.state.lastMessageSuggestions) {
        context.state.lastMessageSuggestions = null
        context.state.lastMessageSuggestionsTitle = null
        context.state.lastMessageSuggestionsOnly = false
      } else {
        context.state.lastMessageSuggestions = suggestionsMessage
          ? suggestionsMessage.suggestions
          : null
        context.state.lastMessageSuggestionsOnly = suggestionsMessage
          ? suggestionsMessage.suggestions_disallow_other_input
          : false
        context.state.lastMessageSuggestionsTitle =
          suggestionsMessage && suggestionsMessage.suggestions_title
            ? suggestionsMessage.suggestions_title[context.state.language]
            : null
      }
    },
    [actions.UPDATE_SEMANTIC_SUGGESTIONS]: (context, suggestionsMessage) => {
      if (!suggestionsMessage && context.state.lastMessageSemanticSuggestions) {
        context.state.lastMessageSemanticSuggestions = null
        context.state.lastMessageHiddenSemanticSuggestions = null
      } else {
        context.state.lastMessageSemanticSuggestions = suggestionsMessage
          ? suggestionsMessage.semanticSuggestions
          : null
        context.state.lastMessageHiddenSemanticSuggestions = suggestionsMessage
          ? suggestionsMessage.semanticSuggestionsMap
          : null
      }
    },
    [actions.SET_OCCUPIED_DATES]: (context, dates) => {
      context.state.lastMessageOccupiedDates = dates
    },
    [actions.EMIT_USER_LOGIN_MESSAGE]: () => {},
    [actions.EMIT_USER_SLOT_SET_MESSAGE]: () => {},
    [actions.EMIT_NEW_USER_MESSAGE]: (context, message) => {
      context.dispatch(actions.UPDATE_SUGGESTIONS, null).then(() => {})
      context.commit(
        mutations.ADD_SOCKET_MESSAGE,
        'emit to SEND_MESSAGE: ' + message
      )
      context.dispatch(actions.SHOW_THINKING_MESSAGE).then(() => {})
      context.commit(mutations.SOCKET_EMIT_NEW_USER_MESSAGE, message)
    },
    [actions.EMIT_STOP_MESSAGE]: () => {},
    [actions.EMIT_REGENERATE_MESSAGE]: (context, originalMessageId) => {
      context.commit(mutations.EMIT_REGENERATE_MESSAGE, originalMessageId)
    },
    [actions.ENABLE_CHAT_BOT]: (context, data) => {
      context.commit(mutations.ENABLE_CHAT_BOT, data)
    },
    [actions.ENABLE_LIVE_CHAT]: (context, data) => {
      context.commit(mutations.ENABLE_LIVE_CHAT, data)
    },
    [actions.ADD_NOTIFICATION_MESSAGE]: (context, message) => {
      context.state.socketMessages.push(message)
      context.state.messageList.push(message)
      context.state.lastMessageDate = new Date().getTime()
    },
    [actions.EMIT_USER_LOGIN_MESSAGE]: () => {},
    [actions.EMIT_USER_SLOT_SET_MESSAGE]: () => {},
    [actions.REQUEST_SEMANTIC_SUGGESTIONS]: () => {},
    [actions.EMIT_USER_RESTART_MESSAGE]: context => {
      context.dispatch(actions.UPDATE_SUGGESTIONS, null).then(() => {})
      context.commit(mutations.CLEAR_MESSAGES)
    },
    [actions.EMIT_USER_MESSAGE_FEEDBACK]: () => {
      // context.commit(mutations.CLEAR_MESSAGES)
    },
    [actions.EMIT_SENT_FEEDBACK]: () => {
      // context.commit(mutations.CLEAR_MESSAGES)
    },
    [actions.TOGGLE_TYPING]: (context, state) => {
      context.state.userTyping = state
    },
    [actions.ENABLE_INPUT_FIELD]: (context, data) => {
      context.commit(mutations.ENABLE_INPUT_FIELD, data)
    },
    // [actions.EMIT_NEW_USER_MESSAGE]: ({ commit }, message) => {
    //   commit(mutations.ADD_SOCKET_MESSAGE, 'emit to SEND_MESSAGE: ' + message)
    //   commit(mutations.SOCKET_EMIT_NEW_USER_MESSAGE, message)
    // },
    [actions.SET_POST_MESSAGE_TARGET]: (context, postMessageTarget) => {
      context.commit(mutations.SET_POST_MESSAGE_TARGET, postMessageTarget)
    },
    [actions.SHOW_PROACTIVE_MESSAGE]: (context, message) => {
      context.commit(mutations.SHOW_PROACTIVE_MESSAGE, message)
    },

    [actions.SET_ENABLED_LANGUAGES]: (context, languages) => {
      context.state.enabledLanguages = languages
      if (!languages.includes(context.rootState.language.split('-')[0])) {
        context
          .dispatch(actions.SET_LANGUAGE, languageToLocale[languages[0]])
          .then(r => r)
      }
    },
    [actions.ADD_UPLOADED_FILES]: (context, files) => {
      context.state.fileList.push(files)
    },
    [actions.SHOW_THINKING_MESSAGE]: context => {
      // Clear any existing timeout
      if (context.state.typingIndicatorTimeout) {
        clearTimeout(context.state.typingIndicatorTimeout)
      }

      // Set a new timeout to show the thinking message after 2 seconds if copilot chat
      context.state.typingIndicatorTimeout = setTimeout(() => {
        if (
          context.rootState.isCopilotChat &&
          !context.state.operatorTookOver &&
          (context.state.messageList.length < 1 ||
            (context.state.messageList.slice(-1)[0].author !==
              constants.AUTHOR_BOT &&
              context.state.messageList.slice(-1)[0].author !==
                constants.AUTHOR_SYSTEM &&
              context.state.messageList.slice(-1)[0].author !==
                constants.AUTHOR_OPERATOR))
        ) {
          // && context.rootState.isCopilotChat
          context.state.showThinkingMessage = true

          // Create a "thinking" message
          const thinkingMessage = new Message(
            'bot',
            context.state.sessionId,
            ' ',
            'in-progress'
          )
          thinkingMessage.config = {
            data: {
              loading_message: { text: i18nInstance.t('thinking_message') }
            }
          }
          thinkingMessage.isThinkingMessage = true

          // Add the message to the list
          context.commit(mutations.ADD_MESSAGE, thinkingMessage)
        }
      }, 2000)
    },
    [actions.HIDE_THINKING_MESSAGE]: context => {
      // Clear the timeout
      if (context.state.typingIndicatorTimeout) {
        clearTimeout(context.state.typingIndicatorTimeout)
        context.state.typingIndicatorTimeout = null
      }

      // Remove the thinking message if it exists
      if (context.state.showThinkingMessage) {
        context.state.showThinkingMessage = false

        // Remove the thinking message from the list
        const thinkingMessageIndex = context.state.messageList.findIndex(
          message => message.id === 'thinking-message'
        )

        if (thinkingMessageIndex !== -1) {
          context.state.messageList.splice(thinkingMessageIndex, 1)
        }
      }
    }
  },
  getters: {
    isRestricted: state => {
      return state.isRestricted
    },
    sessionId: state => {
      return state.sessionId
    },
    enableChatBot: state => {
      return state.enableChatBot
    },
    enableLiveChat: state => {
      return state.enableLiveChat
    },
    enableInputField: state => {
      return state.enableInputField
    },
    enableInputSuggestions: state => {
      return state.enableInputSuggestions
    },
    quickReplies: state => {
      return (
        (state.messageList.length > 0
          ? state.messageList
              .filter(x => {
                if (Array.isArray(x)) {
                  return false
                } else {
                  return !x.isSystemMessage()
                }
              })
              .slice(-1)[0].quick_replies
          : []) || []
      )
    },
    lastMessageSuggestions: state => {
      return state.lastMessageSuggestions
    },
    lastMessageSuggestionsTitle: state => {
      return state.lastMessageSuggestionsTitle
    },
    showBotIsTypingIndicator: state => {
      // If hideInitialTypingIndicator is true and there are no messages, don't show the indicator
      if (isProjectMode && state.messageList.length < 1) {
        return false
      }

      return (
        !state.operatorTookOver &&
        (state.messageList.length < 1 ||
          (state.messageList.slice(-1)[0].author !== constants.AUTHOR_BOT &&
            state.messageList.slice(-1)[0].author !== constants.AUTHOR_SYSTEM &&
            state.messageList.slice(-1)[0].author !==
              constants.AUTHOR_OPERATOR))
      )
    },
    lastMessageId: state => {
      return state.messageList.slice(-1)[0].id
    },
    lastTextMessageId: state => {
      const textMessages = state.messageList.filter(
        message => message.text !== undefined
      )
      return textMessages.slice(-1)[0].id
    },
    shouldHideinitialTypingIndicator: state => {
      console.log(
        'shouldHideinitialTypingIndicator',
        state.hideInitialTypingIndicator,
        state.messageList.length < 1
      )
      return (
        state.hideInitialTypingIndicator === true &&
        state.messageList.length < 1
      )
    },
    botMessageInProgress: state => {
      if (state.regeneratedMessageFrom) {
        const foundMessageIndex = state.messageList.findIndex(m => {
          return (
            m.id === state.regeneratedMessageFrom ||
            (Array.isArray(m) &&
              m[0].timestamp === state.regeneratedMessageFrom) ||
            (Array.isArray(m[0]) &&
              m[0][1].timestamp === state.regeneratedMessageFrom)
          )
        })

        if (foundMessageIndex !== -1) {
          if (Array.isArray(state.messageList[foundMessageIndex])) {
            if (Array.isArray(state.messageList[foundMessageIndex][1])) {
              return (
                state.messageList[foundMessageIndex][1] &&
                (state.messageList[foundMessageIndex][1][1].id ===
                  'in-progress' ||
                  state.messageList[foundMessageIndex][1][1].id ===
                    'progress-placeholder')
              )
            }
            return (
              state.messageList[foundMessageIndex][1] &&
              (state.messageList[foundMessageIndex][1].id === 'in-progress' ||
                state.messageList[foundMessageIndex][1].id ===
                  'progress-placeholder')
            )
          }
        }
      }
      return (
        state.messageList.slice(-1)[0] &&
        state.messageList.slice(-1)[0].id === 'in-progress'
      )
    },
    lastMessageHasLoadingIndicator: state => {
      return (
        state.messageList.slice(-1)[0]?.config?.data?.loading_message?.text !==
        undefined
      )
    },
    showOperatorIsTypingIndicator: state => {
      return state.operatorTyping
    },
    showWebSearchProgress: state => {
      return Boolean(
        state.webSearchProgress && state.webSearchProgress.phase === 'started'
      )
    }
  },
  mutations: {
    [mutations.SOCKET_EMIT_NEW_USER_MESSAGE]: async (state, message) => {
      if (
        !message.text.includes('/chitchat.greet') &&
        !message.text.includes('/ticket.human_handover') &&
        !message.text.includes('Starte mit Terminvereinbarung')
      ) {
        state.messageList.push(message)
      }
    },
    [mutations.ADD_MESSAGE]: (state, message) => {
      state.messageList.push(message)
    },
    [mutations.ADD_MESSAGES]: (state, messages) => {
      const newMessageList = []

      // eslint-disable-next-line no-unused-vars
      messages.forEach((message, index) => {
        const customData = getCustomData(message)
        if (customData.is_regenerated && customData.is_regenerated_from) {
          let foundMessageIndex = newMessageList.findIndex(m => {
            return (
              m.timestamp === customData.is_regenerated_from ||
              (Array.isArray(m) &&
                m[0].timestamp === customData.is_regenerated_from) ||
              (Array.isArray(m[0]) &&
                m[0][1].timestamp === customData.is_regenerated_from)
            )
          })

          if (foundMessageIndex !== -1) {
            const originalMessage = newMessageList[foundMessageIndex]
            const originalsPreviousMessage =
              newMessageList[foundMessageIndex - 1]

            const listRegex = /<ul[^>]*>/g
            const sourcesRegex = /Quellen:\s*<ul[^>]*>(.*?)<\/ul>/g

            const originalMatches =
              originalsPreviousMessage &&
              Boolean(originalsPreviousMessage.text.match(listRegex)) &&
              Boolean(originalsPreviousMessage.text.match(sourcesRegex))

            const restOfMessages = newMessageList.slice(foundMessageIndex)
            const regeneratedPreviousSourceMessage = restOfMessages.find(m =>
              checkIfSoloSourceMessage(m)
            )

            if (originalMatches && regeneratedPreviousSourceMessage) {
              const newStructure = [
                [originalsPreviousMessage, originalMessage],
                [regeneratedPreviousSourceMessage, message]
              ]
              const test = newMessageList.findIndex(
                m => m.timestamp === regeneratedPreviousSourceMessage.timestamp
              )
              newMessageList.splice(test, 1)

              newMessageList.splice(foundMessageIndex - 1, 2, newStructure)
            } else if (originalMatches) {
              foundMessageIndex -= 1 // Adjust for the removal
              newMessageList.splice(foundMessageIndex - 2, 2, [
                [originalsPreviousMessage, originalMessage],
                message
              ])
            } else {
              newMessageList.splice(foundMessageIndex, 1, [
                originalMessage,
                message
              ])
            }
          }
        } else {
          newMessageList.push(message)
        }
      })
      state.messageList = newMessageList
    },
    [mutations.CLEAR_FILES]: state => {
      state.fileList = []
    },
    [mutations.SOCKET_ON_SESSION_CONFIRM]: (state, sessionId) => {
      state.sessionId = sessionId
    },
    [mutations.SOCKET_ON_CONNECT]: state => {
      state.connected = true
      state.socketMessages.push('socket connected')
    },
    [mutations.SOCKET_ON_CONNECT_ERROR]: (state, error) => {
      state.connected = false
      state.socketMessages.push(`socket connection error: ${error}`)
    },
    [mutations.SOCKET_ON_RECONNECT_ATTEMPTS]: (state, count) => {
      state.connected = false
      state.socketMessages.push(`socket reconnection attempt: ${count}`)
    },
    [mutations.SOCKET_ON_MESSAGE]: (state, message) => {
      const customData = getCustomData(message)

      if (
        customData.is_regenerated &&
        customData.is_regenerated_from &&
        message.type !== 'unknown'
      ) {
        const newMessageListCopy = state.messageList.slice()
        let foundMessageIndex = newMessageListCopy.findIndex(
          m =>
            m.timestamp === customData.is_regenerated_from ||
            (Array.isArray(m) &&
              m[0].timestamp === customData.is_regenerated_from) ||
            (Array.isArray(m[0]) &&
              m[0][1].timestamp === customData.is_regenerated_from)
        )
        if (foundMessageIndex !== -1) {
          const originalMessage = newMessageListCopy[foundMessageIndex]

          if (Array.isArray(originalMessage)) {
            if (Array.isArray(originalMessage[1])) {
              newMessageListCopy.splice(foundMessageIndex, 1, [
                originalMessage[0],
                [originalMessage[1][0], message]
              ])
              state.messageList = newMessageListCopy
            } else if (Array.isArray(originalMessage[0])) {
              const regeneratedPreviousMessage = newMessageListCopy.slice(-1)[0]

              if (checkIfSoloSourceMessage(regeneratedPreviousMessage)) {
                newMessageListCopy[foundMessageIndex] = [
                  originalMessage[0],
                  [regeneratedPreviousMessage, message]
                ]
                newMessageListCopy.splice(state.messageList.length - 1, 1)
                state.messageList = newMessageListCopy
              } else {
                newMessageListCopy[foundMessageIndex] = [
                  originalMessage[0],
                  message
                ]
                state.messageList = newMessageListCopy
              }
            } else {
              state.messageList.splice(foundMessageIndex, 1, [
                originalMessage[0],
                message
              ])
            }
          } else {
            state.messageList.splice(foundMessageIndex, 1, [
              originalMessage,
              message
            ])
          }
        }
        state.regeneratedMessageFrom = null
        state.socketMessages.push(message)
      } else if (
        state.messageList.length > 0 &&
        state.messageList.slice(-1)[0].id === 'in-progress'
      ) {
        state.messageList.splice(-1, 1, message)
        state.socketMessages.splice(-1, 1, message)
      } else {
        state.messageList.push(message)
        state.socketMessages.push(message)
      }
    },
    [mutations.SOCKET_ON_REPLACE_LAST_MESSAGE]: (state, message) => {
      if (message.metadata && message.metadata.is_regenerated_from) {
        const newMessageListCopy = state.messageList.slice()
        const foundMessageIndex = state.messageList.findIndex(m =>
          matchMessage(m, message.metadata)
        )
        if (foundMessageIndex !== -1) {
          const originalMessage = newMessageListCopy[foundMessageIndex]

          if (Array.isArray(originalMessage)) {
            if (Array.isArray(originalMessage[1])) {
              message.text = originalMessage[1][1].text + message.text
              newMessageListCopy.splice(foundMessageIndex, 1, [
                originalMessage[0],
                [originalMessage[1][0], message]
              ])
              state.messageList = newMessageListCopy
            } else if (Array.isArray(originalMessage[0])) {
              const regeneratedPreviousMessage = newMessageListCopy.slice(-1)[0]

              if (checkIfSoloSourceMessage(regeneratedPreviousMessage)) {
                newMessageListCopy[foundMessageIndex] = [
                  originalMessage[0],
                  [regeneratedPreviousMessage, message]
                ]
                newMessageListCopy.splice(state.messageList.length - 1, 1)
                state.messageList = newMessageListCopy
              } else {
                newMessageListCopy[foundMessageIndex] = [
                  originalMessage[0],
                  message
                ]
                state.messageList = newMessageListCopy
              }
            } else if (originalMessage[1].id === 'progress-placeholder') {
              newMessageListCopy[foundMessageIndex] = [
                originalMessage[0],
                message
              ]
              state.messageList = newMessageListCopy
            } else {
              message.text = originalMessage[1].text + message.text
              newMessageListCopy.splice(foundMessageIndex, 1, [
                originalMessage[0],
                message
              ])
              state.messageList = newMessageListCopy
            }
          } else {
            state.messageList.splice(foundMessageIndex, 1, [
              originalMessage,
              message
            ])
            state.messageList = newMessageListCopy
          }
        }
      } else if (
        // if last message id is the same as the new message id, add the text to the last message
        state.messageList.length > 0 &&
        state.messageList.slice(-1)[0].id === message.id
      ) {
        state.messageList.splice(-1, 1, message)
      } else {
        // else, push the new message to the message list
        state.messageList.push(message)
      }
    },
    [mutations.SOCKET_ON_ERROR]: (state, error) => {
      state.connected = false
      state.socketMessages.push(`socket error : ${error}`)
    },
    [mutations.SOCKET_ON_DISCONNECT]: state => {
      global.isRestricted = false
      state.connected = false
    },
    [mutations.SOCKET_ON_JOIN_CONFIRM]: (state, data) => {
      state.operatorTakeOver = true
      state.operatorAvatarSrc = data['operator_avatar_src']
    },
    [mutations.SOCKET_ON_OPERATOR_TYPING]: (state, isTyping) => {
      state.operatorTyping = isTyping
      state.operatorTookOver = true
    },
    [mutations.SOCKET_ON_LEAVE_CONFIRM]: state => {
      state.operatorTakeOver = false
      state.operatorAvatarSrc = ''
    },
    [mutations.ADD_SOCKET_MESSAGE]: (state, message) => {
      state.socketMessages.push(message)
    },
    [mutations.CLEAR_MESSAGES]: state => {
      state.messageList = []
      state.webSearchProgress = null
    },
    [mutations.SOCKET_ON_WEB_SEARCH_PROGRESS]: (state, payload) => {
      state.webSearchProgress = payload
    },
    [mutations.IS_RESTRICTED]: (state, data) => {
      state.isRestricted = data
    },
    [mutations.EMIT_REGENERATE_MESSAGE]: (state, originalMessageId) => {
      const foundMessageIndex = state.messageList.findIndex(
        m => m.timestamp === originalMessageId
      )
      const originalMessage = state.messageList[foundMessageIndex]
      let message = new Message(
        'bot',
        state.sessionId,
        '',
        'progress-placeholder'
      )
      const messageListCopy = state.messageList.slice()
      const originalsPreviousMessage = messageListCopy[foundMessageIndex - 1]
      const listRegex = /<ul[^>]*>/g
      const sourcesRegex = /Quellen:\s*<ul[^>]*>(.*?)<\/ul>/g

      const originalMatches =
        originalsPreviousMessage &&
        Boolean(originalsPreviousMessage.text.match(listRegex)) &&
        Boolean(originalsPreviousMessage.text.match(sourcesRegex))

      if (originalMatches) {
        const newStructure = [
          [originalsPreviousMessage, originalMessage],
          message
        ]
        messageListCopy.splice(foundMessageIndex - 1, 2, newStructure)
      } else {
        messageListCopy[foundMessageIndex] = [
          state.messageList[foundMessageIndex],
          message
        ]
      }

      state.messageList = messageListCopy
      state.regeneratedMessageFrom = originalMessageId
    },
    [mutations.ENABLE_CHAT_BOT]: (state, data) => {
      state.enableChatBot = data
    },
    [mutations.ENABLE_LIVE_CHAT]: (state, data) => {
      state.enableLiveChat = data
    },
    [mutations.ENABLE_INPUT_FIELD]: (state, data) => {
      state.enableInputField = data
    },
    [mutations.SET_POST_MESSAGE_TARGET]: (state, postMessageTarget) => {
      state.postMessageTarget = postMessageTarget
    },
    [mutations.SHOW_PROACTIVE_MESSAGE]: (state, message) => {
      const _message = {
        message: 'show',
        type: 'proactiveMessage',
        content: message
      }

      window.parent.postMessage(_message, '*')
    },
    [mutations.SOCKET_ON_LANGUAGE_CHANGE]: (state, sessionId) => {
      state.sessionId = sessionId
    },
    [mutations.SOCKET_ON_DOCUMENT_INDEXED]: (state, payload) => {
      state.documentIndexedEvents.push(payload)
    },
    [mutations.REMOVE_DOCUMENT_INDEXED_EVENT]: (state, documentId) => {
      state.documentIndexedEvents = state.documentIndexedEvents.filter(
        e => e.document_id !== documentId
      )
    }
  }
}

export default b4uSocketModule
